import React, { useState, useEffect, useRef } from 'react';
import Radio from 'antd/lib/radio';
import DatePicker from 'antd/lib/date-picker';
import Spin from 'antd/lib/spin';
import Button from 'antd/lib/button';
import Tooltip from 'antd/lib/tooltip';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useLocation, useHistory } from 'react-router-dom';
import queryString from 'query-string';
import { CSVLink } from 'react-csv';
import { getDiagnosticsData, getDiagnosticsDataPoints, getCSVData } from '../../../../API';
import * as util from '../../../../utils/urlParams';
import { MonitoringLineChart } from './components/MonitoringLineChart';
import { MonitoringModal } from './components/MonitoringModal';
import { config } from '../../../../config';

import { formatStatusValue } from '../../../../utils/formatting';

import openWeatherLogo from '../../../../icons/OpenWeatherLogo.png';
import darkOpenWeatherLogo from '../../../../icons/DarkOpenWeatherLogo.png';
import editIcon from './components/assets/edit.svg';
import downloadIcon from './components/assets/download.svg';
import arrowLeft from './components/assets/Arrowleft.svg';
import arrowRight from './components/assets/Arrowright.svg';
import warningBuildingDark from '../../../../images/alerts-not-setup-dark.png';
import warningBuildingLight from '../../../../images/alerts-not-setup-light.png';

import { Dropdown as PDropdown } from '../../../UI/Dropdown';

import { colors, COLORS_QTY } from '../../../UI/ColorObject';

import './MonitoringDetails.less';

const { REACT_APP_BUILD_ENV } = config;

export const MonitoringDetails = (props) => {
  const {
    buildingId,
    activeCategory: {
      data_api_location: dataApiLocation,
      api_location: apiLocation,
      diagnostic_category: { slug, display_name: categoryDisplayName },
    },
    isSupervisor,
    theme,
  } = props;

  const [view, setView] = useState('custom');
  const [isLoading, setIsLoading] = useState(true);

  const [calendarDateRange, setCalendarDateRange] = useState(['', '']);
  const [graphData, setGraphData] = useState({});
  const [categoryDataPoints, setCategoryDataPoints] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [CSVData, setCSVData] = useState([]);
  const [csvHeaders, setCsvHeaders] = useState();
  const [colorObj, setColorObj] = useState({});
  const { search } = useLocation();
  const { start, end, data_points, period } = queryString.parse(search);
  const history = useHistory();
  const { RangePicker } = DatePicker;
  const csvLink = useRef(null);

  const isStaff = (() => {
    try {
      return !!JSON.parse(localStorage.getItem('current_user') || '{}')?.is_staff;
    } catch {
      return false;
    }
  })();

  


  const detailsURL = (start, end, data_points, period) => {
    const params = {
      start,
      end,
      data_points: data_points ? data_points.join(',') : null,
      period,
      category: categoryDisplayName
    }

    const filtered = Object.fromEntries(
      // eslint-disable-next-line
      Object.entries(params).filter(([_, value]) => Boolean(value))
    );

    return `/monitoring/${buildingId}/?${new URLSearchParams(filtered).toString()}`;
  }

  const urlDataPoints = data_points ? data_points.split(',') : [];

  const fetchCSVData = async () => {
    const diff = moment(end).diff(moment(start), 'days', true);
    const interval = diff > 31 ? 'hour' : 'fifteen';
    const tags = data_points?.split(',');
    const csvData = await getCSVData(
      REACT_APP_BUILD_ENV,
      buildingId,
      slug,
      start,
      end,
      interval,
      tags,
    );

    function moveTimestampFirst(headers) {
      const index = headers.findIndex((obj) => obj.key === 'timestamp');

      if (index > 0) {
        const [timestampObj] = headers.splice(index, 1);
        headers.unshift(timestampObj);
      }

      return headers;
    }

    if (csvData) {
      const headers = Object.keys(csvData[0])
        .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
        .map((key) => ({
          label: key,
          key,
        }));
      setCsvHeaders(moveTimestampFirst(headers));
      setCSVData(csvData);

      setTimeout(() => {
        csvLink.current.link.click();
      }, 100);
    }
  };

  const setLineColorObj = (dataPoints) => {
    const customDataPoints = util.getDataPointsFromUrlParams(search);
    const tempColorObj = {};
    const colors = dataPoints.map((p, i) => `line${i % COLORS_QTY}`);

    if (!customDataPoints.length) {
      let defaultDataPoints = dataPoints.filter((dataPoint) => dataPoint.is_default === true);
      if (categoryDisplayName === 'All') {
        defaultDataPoints = dataPoints.filter((dataPoint) => dataPoint.tag === 'CLDOAT');
      }
      if (!defaultDataPoints.length) {
        defaultDataPoints = dataPoints;
      }
      defaultDataPoints.map((dataPoint) => {
        tempColorObj[dataPoint.tag] = colors.shift();
        return tempColorObj;
      });
    } else {
      customDataPoints.sort((a, b) => a.localeCompare(b))
        .map((dataPoint) => {
        tempColorObj[dataPoint] = colors.shift();
        return tempColorObj;
      });
    }
    setColorObj(tempColorObj)
  };

  async function fetchMonitoringDetailsData() {
    if (period) {
      setView(period.replace('_', ' '));
    }
    setGraphData({});
    setIsLoading(true);
    const monitoringDataPoints = await getDiagnosticsDataPoints(REACT_APP_BUILD_ENV, apiLocation);
    const monitoringDefaultDataPoints = monitoringDataPoints.data_points.filter(
      (d) => d.is_default === true || d.tag === 'CLDOAT',
    );

    const categoryDataPoints = monitoringDataPoints.data_points.map((r) => {
      let state;
      const tagsSelected = urlDataPoints.length > 0;
      if (r.activeCheckbox) {
        state = r.activeCheckbox;
      } else if ((tagsSelected && urlDataPoints.includes(r.tag)) || (!tagsSelected && r.tag === 'CLDOAT')) {
        state = true;
      } else {
        state = false;
      }
      return { activeCheckbox: state, ...r };
    });

    setCategoryDataPoints(categoryDataPoints);

    let customData;
    if (!data_points) {
      customData = monitoringDefaultDataPoints.length ? monitoringDefaultDataPoints.map((p) => p.tag) : [];
    } else {
      customData = data_points.split(',');
    }

    // add time range and points if not in URL
    const newStart = start || moment().subtract(1, 'hour').utc().format();
    const newEnd = end || moment().utc().format();

    if (!(start && end && urlDataPoints.length > 0)) {
      // time is set, switch view and reset calendar
      if (newStart !== start || newEnd !== end) {
        setView('1 hour')
        setCalendarDateRange(['', '']);
      }
      history.replace(detailsURL(newStart, newEnd, customData));
    }

    // opened with a url, view is not set, default to custom and set calendar
    if (view === 'custom' && calendarDateRange[0] === '') {
      setCalendarDateRange([moment(newStart), moment(newEnd)]);
    } else if (view !== 'custom') setCalendarDateRange(['', '']);

    const monitoringGraphData = await getDiagnosticsData(
      REACT_APP_BUILD_ENV,
      dataApiLocation,
      customData,
      newStart,
      newEnd,
    );

    setLineColorObj(monitoringGraphData.data_points);
    setGraphData(monitoringGraphData);
    setIsLoading(false);
  }

  useEffect(() => {
    if (apiLocation && buildingId) fetchMonitoringDetailsData();
  }, [apiLocation, start, end]);


  const handleViewChange = (selectedView) => {
    setView(selectedView);

    const endTime = moment().utc().format();
    let startTime;

    switch (selectedView) {
      case '1 hour':
        startTime = moment(endTime).subtract(1, 'hour').utc().format();
        break;
      case '6 hours':
        startTime = moment(endTime).subtract(6, 'hours').utc().format();
        break;
      case '24 hours':
        startTime = moment(endTime).subtract(24, 'hours').utc().format();
        break;
      case '7 days':
        startTime = moment(endTime).subtract(7, 'days').utc().format();
        break;
      case '30 days':
        startTime = moment(endTime).subtract(30, 'days').utc().format();
        break;
      default:
        startTime = moment(endTime).subtract(1, 'hour').utc().format();
        break;
    }

    const selectedPeriod = selectedView.replace(' ', '_');

    history.push(detailsURL(startTime, endTime, urlDataPoints, selectedPeriod));

    setCalendarDateRange(['', '']);
  };

  const handleCalendarChange = (dates, dateStrings) => {

    if (!dates) {
      setCalendarDateRange(['', '']);
      handleViewChange('1 hour')
      return;
    }

    let startTime;
    let endTime;

    if (!dateStrings[0] || !dateStrings[1]) {
      setCalendarDateRange(dates);
      return;
    }
    
    if (!dateStrings[0] && dateStrings[1] === moment().format('YYYY-MM-DD')) {
      startTime = moment(dateStrings[1]).startOf('day').utc().format();
      endTime = moment().utc().format();
    }
    else if (!dateStrings[0] && dateStrings[1]) {
      startTime = moment(dateStrings[1]).startOf('day').utc().format();
      endTime = moment(dateStrings[1]).endOf('day').utc().format();
    }
    else if (dateStrings[1] === moment().format('YYYY-MM-DD')) {
      startTime = moment(dateStrings[0]).startOf('day').utc().format();
      endTime = moment().utc().format();
    }
    else if (dateStrings[0] && dateStrings[1]) {
      startTime = moment(dateStrings[0]).startOf('day').utc().format();
      endTime = moment(dateStrings[1]).endOf('day').utc().format();
    }

    history.push(detailsURL(startTime, endTime, urlDataPoints));
    setCalendarDateRange([moment(startTime), moment(endTime)]);
    setView('custom');
  };

  const scrollView = (direction) => {
    let newStartTime;
    let newEndTime;
    let timeDiff;
    let newEndMoment;
    
    const now = moment();

    if (direction === 'back') {
      switch (view) {
        case '1 hour':
          newStartTime = moment(start).subtract(1, 'hour').utc().format();
          newEndTime = moment(end).subtract(1, 'hour').utc().format();
          break;
        case '6 hours':
          newStartTime = moment(start).subtract(6, 'hours').utc().format();
          newEndTime = moment(end).subtract(6, 'hours').utc().format();
          break;
        case '24 hours':
          newStartTime = moment(start).subtract(24, 'hours').utc().format();
          newEndTime = moment(end).subtract(24, 'hours').utc().format();
          break;
        case '7 days':
          newStartTime = moment(start).subtract(7, 'days').utc().format();
          newEndTime = moment(end).subtract(7, 'days').utc().format();
          break;
        case '30 days':
          newStartTime = moment(start).subtract(30, 'days').utc().format();
          newEndTime = moment(end).subtract(30, 'days').utc().format();
          break;
        case 'custom':
          timeDiff = calendarDateRange[1].diff(calendarDateRange[0], 'days') || 1;
          newStartTime = moment(start).subtract(timeDiff, 'days').utc().format();
          newEndTime = moment(end).subtract(timeDiff, 'days').utc().format();
          break;
        default:
          timeDiff = calendarDateRange[1].diff(calendarDateRange[0], 'days');
          newStartTime = moment(start).subtract(timeDiff, 'days').utc().format();
          newEndTime = moment(end).subtract(timeDiff, 'days').utc().format();
          break;
      }
    } else {
      switch (view) {
        case '1 hour':
          newStartTime = moment(start).add(1, 'hour').utc().format();
          newEndTime = moment(end).add(1, 'hour').utc().format();
          break;
        case '6 hours':
          newStartTime = moment(start).add(6, 'hours').utc().format();
          newEndTime = moment(end).add(6, 'hours').utc().format();
          break;
        case '24 hours':
          newStartTime = moment(start).add(24, 'hours').utc().format();
          newEndTime = moment(end).add(24, 'hours').utc().format();
          break;
        case '7 days':
          newStartTime = moment(start).add(7, 'days').utc().format();
          newEndTime = moment(end).add(7, 'days').utc().format();
          break;
        case '30 days':
          newStartTime = moment(start).add(30, 'days').utc().format();
          newEndTime = moment(end).add(30, 'days').utc().format();
          break;
        case 'custom':
          timeDiff = calendarDateRange[1].diff(calendarDateRange[0], 'days') || 1;
          newStartTime = moment(start).add(timeDiff, 'days').utc().format();

          newEndMoment = moment(end).add(timeDiff, 'days');

          newEndTime = newEndMoment.isAfter(now) ? now.utc().format() : moment(end).add(timeDiff, 'days').utc().format();
          break;
        default:
          timeDiff = calendarDateRange[1].diff(calendarDateRange[0], 'days');
          newStartTime = moment(start).add(timeDiff, 'days').utc().format();
          newEndTime = moment(end).add(timeDiff, 'days').utc().format();
          break;
      }
    }

    setCalendarDateRange([moment(newStartTime), moment(newEndTime)]);
    history.push(detailsURL(newStartTime, newEndTime, urlDataPoints));
  };

  const disableScroll = () => {
    const endTimeHour = moment(end).utc().format('dddd, MMMM Do YYYY, h');
    const requestedHour = moment().utc().format('dddd, MMMM Do YYYY, h');
    return endTimeHour === requestedHour;
  };

  const disabledDates = (current) => {
    return current && current > moment();
  };

  const onModalClose = () => {
    setIsModalOpen(false);
  };

  const saveChanges = async (dataPoints) => {
    const tempColorObj = {};
    const colors = dataPoints.map((p, i) => `line${i % COLORS_QTY}`);
    const filteredDataPoints = dataPoints.filter((p) => p.activeCheckbox === true).map((p) => p.tag);
    filteredDataPoints.sort((a, b) => a.localeCompare(b)).map((dataPoint) => {
      tempColorObj[dataPoint] = colors.shift();
      return tempColorObj;
    });
    
    setColorObj(tempColorObj)

    setIsModalOpen(false);

    history.push(detailsURL(start, end, filteredDataPoints));

    if (filteredDataPoints.length) {
      setIsLoading(true);
      const monitoringGraphData = await getDiagnosticsData(
        REACT_APP_BUILD_ENV,
        dataApiLocation,
        filteredDataPoints,
        start,
        end,
      );
      setGraphData(monitoringGraphData);
      setIsLoading(false);
    } else {
      setGraphData({});
    }
  };

  const renderLegendItems = () => {
    const customTags = util.getDataPointsFromUrlParams(search);

    if (!customTags?.length) return [];
    
    const customDataPoints = categoryDataPoints.filter((dataPoint) => customTags.includes(dataPoint.tag));

    return customDataPoints.sort((a, b) => a.tag.localeCompare(b.tag)).map((dataPoint,i) => {
      const { status, modbus_register: modbusRegister, display_name: legendDisplayName, tag, is_online } = dataPoint;
      const isOpenWeather = ['CLDOAT', 'CLDOAFT', 'CLDHM'].includes(tag);
      return (
        <div className="monitoring-details-legend__item" key={tag}>
          <div className="line-width">
            <div className='line' style={{borderColor: colors[`line${i % COLORS_QTY}`]}} />
          </div>
          <div className="wide-column">
            {legendDisplayName}
            {isOpenWeather && (
              <a href="https://openweather.co.uk/about" target="_blank" rel="noopener noreferrer">
                {theme === 'light' ? (
                  <img className="monitoring-legends-openweather-icon" src={openWeatherLogo} alt="openweather" />
                ) : (
                  <img className="monitoring-legends-openweather-icon" src={darkOpenWeatherLogo} alt="openweather" />
                )}
              </a>
            )}
          </div>
          <div className="data-tag-width">{tag}</div>
          {isStaff ? <div className="equal-width desktop">{modbusRegister}</div> : null}
          <div className="equal-width latest-reading-col-mobile">
            <span>{formatStatusValue(status)}</span>
            {!isSupervisor ?
              <div className="point-status-mobile-legend">{is_online ? <span className="ps-online">CONNECTED</span> : <span className="ps-offline">DISCONNECTED</span>}</div>
              :
              null}
          </div>

          {
            !isSupervisor ?
              <div className="equal-width point-status-indicator-column">
                <div className="point-status-indicator-legend">{is_online ? <span className="ps-online">CONNECTED</span> : <span className="ps-offline">DISCONNECTED</span>}</div>
              </div>
            :
            null
          }
        </div>
      );
    });
  };
  
  const tooltipText = 'Please note that when you download this data, it is only available in 1 hour intervals.';

  const timeIntervals = [
    {id: 1, name: '1 hour'},
    {id: 2, name: '6 hours'},
    {id: 3, name: '24 hours'},
    {id: 4, name: '7 days'},
    {id: 5, name: '30 days'},
  ]

  return (
    <div className={`monitoring-details monitoring-details-${theme}`}>
      {Object.keys(graphData).length === 0 || graphData.data_points.length < 1 ? null : (
        <div className="monitoring-details-header">
          <div className="md-time-control">
            <div className="monitoring-details-header--text">Time Interval</div>
            <Radio.Group
              value={view}
              onChange={(e) => handleViewChange(e.target.value)}
              className="monitoring-details-header--view"
            >
              <Radio.Button value="1 hour">1 Hour</Radio.Button>
              <Radio.Button value="6 hours">6 Hours</Radio.Button>
              <Radio.Button value="24 hours">24 Hours</Radio.Button>
              <Radio.Button value="7 days">7 Days</Radio.Button>
              <Radio.Button value="30 days">30 Days</Radio.Button>
            </Radio.Group>

            <div className="time-interval-dropdown">
              <PDropdown
                placeholder="All Tiers"
                items={timeIntervals}
                title="name"
                callback={(c) => handleViewChange(c.name)}
                theme={theme}
                filter={view}
                fullWidth={true}
              />
            </div>

            
          </div>
          <div className="md-time-control">
            <div className="monitoring-details-header--text">Date Range</div>
            <div className="monitoring-details-header--datepicker">
              <RangePicker
                value={calendarDateRange}
                onCalendarChange={(dates, dateStrings) => handleCalendarChange(dates, dateStrings)}
                disabledDate={disabledDates}
                inputReadOnly
              />
            </div>

          </div>
        </div>
      )}
      <div className="monitoring-details-row">
        <div className="monitoring-details-card-wrapper">
          <div className="monitoring-details-card">
            {isLoading ? (
              <div className="monitoring-details--loading">
                <Spin size="large" />
              </div>
            ) : (
              <>
                {Object.keys(graphData).length === 0 || graphData.data_points.length < 1 ? (
                  <div className="no-datapoint-container" onClick={() => setIsModalOpen(true)}>
                    <img
                      src={theme === 'dark' ? warningBuildingDark : warningBuildingLight}
                      alt="no data points"
                      className="image-no-datapoints"
                    />
                    <div className="warning-no-datapoints">
                      No Data Points are selected, click
                      <span className="ndp-highlight"> here </span>
                      to manage your Data Points
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="monitoring-details-card--header">
                      <div className="monitoring-details-card--title">
                        {start && end ? `${moment(start).format('MMM DD, YYYY')} – ${moment(end).format('MMM DD, YYYY')}` : ``}
                      </div>
                      <div className="monitoring-details-download">
                        <Tooltip overlayClassName={`app-tooltip-${theme}`} placement="bottom" title={tooltipText}>
                          <div
                            className="monitoring-details-card--download-button"
                            role="presentation"
                            onClick={() => fetchCSVData()}
                          >
                            Download Data
                            <img className="download-img" src={downloadIcon} alt="download-icon" />
                          </div>
                          <div
                            className="monitoring-details-card--download-button-mobile"
                            role="presentation"
                            onClick={() => fetchCSVData()}
                          >
                            <img className="download-img-mobile" src={downloadIcon} alt="download-icon" />
                          </div>
                        </Tooltip>

                        <CSVLink
                          data={CSVData}
                          headers={csvHeaders}
                          filename="monitoring_data.csv"
                          className="hidden"
                          ref={csvLink}
                          target="_blank"
                        />
                      </div>
                    </div>
                    <div className="monitoring-details--body">
                      <Button className="monitoring-details--scroll-button-desktop" data-testid="back-button" onClick={() => scrollView('back')}>
                        <img src={arrowLeft} alt="scroll-left-button" />
                      </Button>
                      <MonitoringLineChart colorObj={colorObj} graphData={graphData} dateRange={{ startTime: start, endTime: end }} theme={theme} />
                      <Button
                        className="monitoring-details--scroll-button-desktop"
                        data-testid="forward-button"
                        onClick={() => scrollView('forward')}
                        disabled={disableScroll()}
                      >
                        <img src={arrowRight} alt="scroll-right-button" />
                      </Button>
                      <div className="monitoring-details--scroll-buttons-mobile">
                        <Button 
                          className="monitoring-details--scroll-button-mobile" 
                          onClick={() => scrollView('back')}
                        >
                          <img src={arrowLeft} alt="scroll-left-button" />
                        </Button>
                        <Button
                          className="monitoring-details--scroll-button-mobile"
                          onClick={() => scrollView('forward')}
                          disabled={disableScroll()}
                        >
                          <img src={arrowRight} alt="scroll-right-button" />
                        </Button>
                      </div>
                    </div>
                    <div className="monitoring-details-card--legend">
                      <div className="monitoring-details-card--header">
                        <div className="monitoring-details-card--title">Legend</div>
                        <div className="monitoring-details-card__button-wrapper">
                          <Button
                            block
                            size="medium"
                            className="monitoring-details-card__button"
                            onClick={() => setIsModalOpen(true)}
                          >
                            <div className="monitoring-details-card__buton-text">
                              <img className="edit-img" src={editIcon} alt="edit-icon" />
                              Manage Data Points
                              {` (${categoryDataPoints.length})`}
                            </div>
                          </Button>
                        </div>
                      </div>
                      <div className="monitoring-details-legend__column-names">
                        <div className="line-width">Line</div>
                        <div className="wide-column">Data Point</div>
                        <div className="data-tag-width">Data Tag</div>
                        {isStaff ? (
                          <div className="equal-width desktop">Modbus Register</div>
                        ) : null}
                        <div className="equal-width">Latest Reading</div>
                        {!isSupervisor ?
                          <div className="equal-width point-status-col-header-mobile">Point Status</div>
                          :
                          null}
                      </div>
                      {renderLegendItems()}
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      {isModalOpen && (
        <MonitoringModal
          isSupervisor={isSupervisor}
          categoryDataPoints={categoryDataPoints}
          setCategoryDataPoints={setCategoryDataPoints}
          onCancel={onModalClose}
          saveChanges={saveChanges}
          theme={theme}
        />
      )}
    </div>
  );
};

MonitoringDetails.propTypes = {
  activeCategory: PropTypes.shape({
    api_location: PropTypes.string,
    buildingId: PropTypes.string,
    data_api_location: PropTypes.string,
    diagnostic_category: PropTypes.shape({
      display_name: PropTypes.string,
      slug: PropTypes.string,
    }),
    display_name: PropTypes.string,
    id: PropTypes.number,
  }).isRequired,
};
